import React, { useEffect, useRef, useState } from 'react';

import { Button, Carousel, Dropdown, Spin, Tooltip } from 'antd';
import { CommentOutlined,
  DislikeOutlined,
  EllipsisOutlined,
  EyeInvisibleOutlined,
  GlobalOutlined,
  LikeOutlined,
  LoadingOutlined,
  MinusOutlined,
  PlusOutlined } from '@ant-design/icons';
import { NavLink } from 'react-router-dom';
import dayjs from 'dayjs';
import type { MenuProps } from 'antd/es/menu';
import { AnyObject } from '@triare/auth-redux';
import clsx from 'clsx';
import { PostShort } from '../../../hooks/api/posts';
import { CommentData } from '../../../hooks/api/comments';
import { FetchGet, PagingDataMeta, PagingDataResponse } from '../../../hooks/fetch';
import { useSearchParams } from '../../../hooks/useSearchParams';

import styles from './index.module.scss';
import { getEntityMeta } from '../../../utils/entityMeta';
import displayUserName from '../../../utils/userName';

export function useUniqueData<T extends { id: string }>(
  get: FetchGet<PagingDataResponse<T>>,
  page: number,
): [PostOrCommentAccList<T>, () => void] {
  const [listAcc, setData] = useState<PostOrCommentAccList<T>>({
    list: [],
    page: 0,
    lastMeta: null,
  });

  useEffect(() => {
    if (get.data?.data && !get.error && !get.loading) {
      if (page === 1) {
        setData({
          list: get.data?.data || [],
          page,
          lastMeta: get.data.meta,
        });
      } else {
        const list = [...(listAcc?.list || [])];

        if (get.data.data && get.data.data.length > 0) {
          get.data.data.forEach((item) => {
            if (!list.some(({ id }) => id === item.id)) {
              list.push(item);
            }
          });
        }

        if ((listAcc?.list || []).length !== list.length) {
          setData({
            list: [...(listAcc?.list || []), ...(get.data.data || [])],
            page,
            lastMeta: get.data.meta,
          });
        }
      }
    }
  }, [get.data?.data]);

  return [
    listAcc,
    () => {
      setData({
        list: [],
        page: 0,
        lastMeta: null,
      });
    },
  ];
}

export interface PostOrCommentAccList<T = AnyObject> extends AnyObject {
  list: T[];
  page: number;
  lastMeta: PagingDataMeta | null;
}

export interface PostOrCommentTemplateProps {
  open?: boolean;
  setOpen?: (value: boolean) => void;
  data: PostShort | CommentData;
  type: 'posts' | 'comments' | 'subComments';
  dropdownMenu: MenuProps;
  childrenList?: React.ReactNode[];
  showMoreText?: string;
  nextPage?: () => void;
  loading?: boolean;
}

export default function PostOrCommentTemplate({
  open,
  setOpen,
  data,
  type,
  dropdownMenu,
  childrenList,
  showMoreText,
  nextPage,
  loading,
}: PostOrCommentTemplateProps): JSX.Element {
  const { searchParams } = useSearchParams();
  const divGallery = useRef<HTMLDivElement | null>(null);
  const meta = getEntityMeta(data as any);

  console.log('PostOrCommentTemplate data:', data);

  useEffect(() => {
    if (divGallery.current) {
      const resize = () => {
        if (divGallery.current) {
          const current = divGallery.current as HTMLDivElement;
          const parent = divGallery.current?.parentNode as HTMLDivElement;
          const { width } = parent.getBoundingClientRect();

          if (width <= 900) {
            current.classList.add(styles.small);
            parent.classList.add(styles.small);
          } else {
            current.classList.remove(styles.small);
            parent.classList.remove(styles.small);
          }
        }
      };

      window.addEventListener('resize', resize, false);

      resize();

      return () => window.removeEventListener('resize', resize);
    }

    return () => {};
  }, [divGallery.current]);

  const isHidden = Boolean(data.isHidden);

  // eslint-disable-next-line max-len
  const showPlus = searchParams.get('isShowHiddenComment') !== 'false' ? data.numberOfCommentsTotal : data.numberOfComments;
  const menu: MenuProps = {
    items: [
      ...(dropdownMenu.items ?? []),
      meta.link && { key: 'open-site', label: 'View on Website', disabled: !meta.link },
    ].filter(Boolean) as MenuProps['items'],
    onClick: ({ key }) => {
      if (key === 'open-site' && meta.link) window.open(meta.link, '_blank', 'noopener,noreferrer');
      dropdownMenu.onClick?.({ key } as any);
    },
  };

  return (
    <div className={styles.postOrCommentTemplate}>
      {type !== 'subComments' ? (
        <div className={clsx(styles.treeCol, { [styles.maxHeight]: !showPlus, [styles.open]: open })}>
          {showPlus && setOpen ? (
            <div
              role="none"
              className={styles.icon}
              onClick={(e) => {
                e.preventDefault();
                setOpen(!open);
              }}
            >
              {
                // eslint-disable-next-line no-nested-ternary
                loading ? (
                  <Spin indicator={<LoadingOutlined spin />} className={styles.spin} />
                ) : (open ? (
                  <MinusOutlined />
                ) : (
                  <PlusOutlined />
                ))
              }
            </div>
          ) : null}
        </div>
      ) : (
        <div />
      )}
      <div className={styles.content}>
        <div className={styles.row}>
          <div className={styles.data}>
            {isHidden ? (
              <div className={styles.hiddenBanner}>
                <EyeInvisibleOutlined />
                <div className={styles.hiddenBannerContent}>
                  <span className={styles.hiddenBannerTitle}>
                    {`Hidden ${type === 'posts' ? 'post' : 'comment'}`}
                    {data.hiddenByUser ? ` by ${displayUserName(data.hiddenByUser)}` : ''}
                    {data.hiddenAt ? ` · ${dayjs(data.hiddenAt).format('DD MMMM, YYYY h:mm a')}` : ''}
                  </span>
                  {data.hiddenReason ? (
                    <span className={styles.hiddenBannerReason}>
                      Reason:
                      {' '}
                      {data.hiddenReason}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}
            <div className={styles.user}>
              <div className={styles.avatar} style={{ backgroundImage: `url(${data.user?.photo?.location})` }} />
              <div className={styles.name}>
                <NavLink to={`/users/${data.user?.id}`}>
                  {data.user ? displayUserName(data.user) : null}
                </NavLink>
              </div>
              {meta.relations.map((rel) => (
                <React.Fragment key={rel.kind}>
                  {rel.title ? (
                    <div className={styles.address}>
                      {rel.link ? (
                        <a href={rel.link} target="_blank" rel="noopener noreferrer">
                          {rel.title}
                        </a>
                      ) : rel.title}
                    </div>
                  ) : null}
                  {rel.kind !== 'unknown' ? (
                    <div className={styles.business}>{rel.kindLabel}</div>
                  ) : null}
                </React.Fragment>
              ))}
              <div className={styles.date}>{dayjs(data.createdAt).format('DD MMMM, YYYY h:mm a')}</div>
            </div>
            <div className={styles.text}>
              <span>{data.text}</span>

              {data?.files?.length ? (
                <div className={styles.gallery} ref={divGallery}>
                  <Carousel arrows={data.files.length > 1} dots={data.files.length > 1}>
                    {data.files?.map(({ id, location, originalName }) => (
                      <div key={id} className={styles.carouselItem}>
                        <img src={location} alt={originalName} className={styles.carouselImg} />
                      </div>
                    ))}
                  </Carousel>
                </div>
              ) : null}
            </div>
            <div className={styles.reaction}>
              <div className={styles.like}>
                <div className={styles.likeUp}>
                  <LikeOutlined />
                  {' '}
                  {data.numberOfLikes}
                </div>
                <div className={styles.likeDown}>
                  <DislikeOutlined />
                  {' '}
                  {data.numberOfDislikes}
                </div>
              </div>
              {type === 'posts' ? (
                <div className={styles.comments}>
                  <CommentOutlined />
                  {' '}
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {'numberOfComments' in data
                    ? (searchParams.get('isShowHiddenComment') !== 'false'
                      ? data.numberOfCommentsTotal
                      : data.numberOfComments || 0)
                    : 0}
                </div>
              ) : null}
              {type === 'comments' ? (
                <div className={styles.comments}>
                  <CommentOutlined />
                  {' '}
                  {'numberOfComments' in data && data.numberOfCommentsTotal}
                </div>
              ) : null}
              {type === 'posts' && 'comments' in data && data.comments && data.comments.length > 0 ? (
                <div className={styles.reply}>
                  <div>
                    <span>Reply to Post:</span>
                    {' '}
                    <NavLink to={`/users/${data.comments[0].user.id}`}>
                      @
                      {displayUserName(data.comments[0].user)}
                    </NavLink>
                  </div>
                  <div className={styles.shortText}>{data.comments[0].text}</div>
                </div>
              ) : null}
            </div>
          </div>

          <div className={styles.actions}>
            <Dropdown menu={menu} trigger={['click']}>
              <Button icon={<EllipsisOutlined style={{ fontSize: 28 }} />} />
            </Dropdown>
            {meta.link ? (
              <Tooltip title="View on Website">
                <Button
                  icon={<GlobalOutlined style={{ fontSize: 20 }} />}
                  onClick={() => window.open(meta.link, '_blank', 'noopener,noreferrer')}
                />
              </Tooltip>
            ) : null}
          </div>
        </div>

        {childrenList && childrenList.length > 0 ? (
          <div className={styles.childrenList}>
            {childrenList}

            {showMoreText && nextPage ? (
              <div
                role="none"
                onClick={(e) => {
                  e.preventDefault();
                  nextPage();
                }}
                className={styles.showMore}
              >
                {showMoreText}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
